/**
 * Expenses log — KPIs, filter chips, DataTable, plus a record form Sheet.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { CreditCard, Pencil, Plus, Filter } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatMoney, formatMoneyCompact } from "@bizop/shared/currency";
import { formatDate } from "@bizop/shared/dates";
import {
  Badge,
  Button,
  DataTable,
  KpiCard,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  StatusPill,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  cn,
  toast,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { ExportButton } from "../../../components/export";
import {
  downloadCsv,
  exportFilename,
  formatDateForCsv,
  toCsv,
  type ExportColumn,
} from "../../../lib/export";
import { useMockData } from "../../../lib/mockMode";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import {
  mockExpenses,
  type MockExpense,
  type MockExpenseCategory,
  type MockExpenseStatus,
} from "../../../lib/financeMock";
import {
  ExpenseForm,
  type ExpenseFormValues,
} from "../../../components/forms/ExpenseForm";
import { RecordAuditBadge } from "../../../components/audit/RecordAuditBadge";
import { RecordAuditTrail } from "../../../components/audit/RecordAuditTrail";
import {
  AttachmentBadge,
  AttachmentList,
} from "../../../components/attachments";

const EXPENSE_PURPOSE_OPTIONS = [
  { value: "receipt", label: "Receipt" },
  { value: "supporting_doc", label: "Supporting Doc" },
  { value: "approval_email", label: "Approval Email" },
];

export const Route = createFileRoute("/_app/finance/expenses")({
  component: ExpensesPage,
});

const STATUS_VARIANT: Record<
  MockExpenseStatus,
  "info" | "warning" | "success" | "danger" | "neutral"
> = {
  pending: "warning",
  approved: "info",
  paid: "success",
  rejected: "danger",
  void: "neutral",
};
const STATUS_LABEL: Record<MockExpenseStatus, string> = {
  pending: "Pending",
  approved: "Approved",
  paid: "Paid",
  rejected: "Rejected",
  void: "Void",
};

const CATEGORY_LABEL: Record<MockExpenseCategory, string> = {
  salary: "Salary",
  rent: "Rent",
  utilities: "Utilities",
  logistics: "Logistics",
  marketing: "Marketing",
  travel: "Travel",
  repairs: "Repairs",
  office_supplies: "Office",
  professional_fees: "Professional",
  tax_duty: "Tax / duty",
  bank_charges: "Bank",
  other: "Other",
};

const ALL_CATEGORIES = Object.keys(CATEGORY_LABEL) as MockExpenseCategory[];
const ALL_STATUSES = Object.keys(STATUS_LABEL) as MockExpenseStatus[];

function fxRateForCurrency(code: string): bigint {
  if (code === "BDT") return 100_000_000n;
  if (code === "USD") return 11_120_000_000n;
  if (code === "EUR") return 12_080_000_000n;
  if (code === "CNY") return 1_555_000_000n;
  return 100_000_000n;
}

function ExpensesPage() {
  const isMock = useMockData();
  void isMock;
  const [rows, setRows] = usePersistentMockState<MockExpense[]>("expenses", mockExpenses);
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState<MockExpense | null>(null);
  const [editingExpense, setEditingExpense] = useState<MockExpense | null>(
    null,
  );

  const [categoryFilter, setCategoryFilter] = useState<MockExpenseCategory | "all">("all");
  const [statusFilter, setStatusFilter] = useState<MockExpenseStatus | "all">("all");

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (categoryFilter !== "all" && r.category !== categoryFilter) return false;
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      return true;
    });
  }, [rows, categoryFilter, statusFilter]);

  // KPIs (current month, status != void/rejected)
  const totalThisMonth = rows
    .filter((r) => r.status !== "void" && r.status !== "rejected")
    .reduce((s, r) => s + r.amountInBaseCents, 0n);
  const pendingCount = rows.filter((r) => r.status === "pending").length;
  const pendingValue = rows
    .filter((r) => r.status === "pending")
    .reduce((s, r) => s + r.amountInBaseCents, 0n);
  const topCategory = useMemo(() => {
    const map = new Map<MockExpenseCategory, bigint>();
    for (const r of rows) {
      if (r.status === "void" || r.status === "rejected") continue;
      map.set(r.category, (map.get(r.category) ?? 0n) + r.amountInBaseCents);
    }
    let best: [MockExpenseCategory, bigint] | null = null;
    for (const entry of map.entries()) {
      if (!best || entry[1] > best[1]) best = entry;
    }
    return best;
  }, [rows]);

  const columns = useMemo<ColumnDef<MockExpense>[]>(
    () => [
      {
        accessorKey: "expenseNumber",
        header: "Expense #",
        cell: ({ row }) => (
          <span className="font-mono text-[12px] text-(--text-primary)">
            {row.original.expenseNumber}
          </span>
        ),
        meta: { mono: true },
      },
      {
        accessorKey: "occurredAt",
        header: "Date",
        cell: ({ row }) => (
          <span className="text-(--text-secondary) tabular-nums">
            {formatDate(row.original.occurredAt, { style: "short" })}
          </span>
        ),
      },
      {
        accessorKey: "category",
        header: "Category",
        cell: ({ row }) => (
          <Badge variant="neutral" size="sm">
            {CATEGORY_LABEL[row.original.category]}
          </Badge>
        ),
      },
      {
        accessorKey: "description",
        header: "Description",
        cell: ({ row }) => (
          <span className="text-(--text-primary) line-clamp-1">
            {row.original.description}
          </span>
        ),
      },
      {
        accessorKey: "branchLabel",
        header: "Branch",
        cell: ({ row }) => (
          <span className="text-(--text-secondary)">{row.original.branchLabel}</span>
        ),
      },
      {
        id: "amount",
        header: "Amount",
        meta: { numeric: true, mono: true },
        cell: ({ row }) => (
          <span className="font-mono tabular-nums text-(--text-primary)">
            {formatMoney(row.original.amountCents, row.original.currency, {
              display: row.original.currency === "BDT" ? "symbol" : "code",
              locale: "en-BD",
            })}
          </span>
        ),
      },
      {
        id: "amountBdt",
        header: "BDT",
        meta: { numeric: true, mono: true, hideOnMobile: true },
        cell: ({ row }) => (
          <span className="font-mono tabular-nums text-(--text-secondary)">
            {formatMoney(row.original.amountInBaseCents, "BDT", { locale: "en-BD" })}
          </span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <StatusPill variant={STATUS_VARIANT[row.original.status]} size="sm">
            {STATUS_LABEL[row.original.status]}
          </StatusPill>
        ),
      },
    ],
    [],
  );

  function handleCreate(values: ExpenseFormValues) {
    const fxRate = fxRateForCurrency(values.currency);
    const inBase = (values.amountCents * fxRate) / 100_000_000n;
    const newRow: MockExpense = {
      _id: `exp_local_${Date.now()}`,
      expenseNumber: `EXP-2026-${String(rows.length + 1).padStart(6, "0")}`,
      category: values.category,
      description: values.description,
      branchId: values.branchId,
      branchLabel:
        values.branchId === "branch_factory_savar"
          ? "Factory — Savar"
          : "Head Office (Dhaka)",
      currency: values.currency,
      amountCents: values.amountCents,
      amountInBaseCents: inBase,
      fxRateToBaseE8: fxRate,
      paymentMethod: values.paymentMethod,
      payeeName: values.payeeName || null,
      occurredAt: new Date(values.occurredAt).getTime(),
      status: "pending",
      notes: values.notes || null,
    };
    setRows((prev) => [newRow, ...prev]);
    setCreateOpen(false);
    toast.success(`${newRow.expenseNumber} recorded (mock)`);
  }

  function handleEditExpense(values: ExpenseFormValues) {
    if (!editingExpense) return;
    if (editingExpense.status === "paid" || editingExpense.status === "void") {
      toast.error("Posted expenses cannot be edited.");
      return;
    }
    const fxRate = fxRateForCurrency(values.currency);
    const inBase = (values.amountCents * fxRate) / 100_000_000n;
    setRows((prev) =>
      prev.map((r) =>
        r._id === editingExpense._id
          ? {
              ...r,
              category: values.category,
              description: values.description,
              branchId: values.branchId,
              branchLabel:
                values.branchId === "branch_factory_savar"
                  ? "Factory — Savar"
                  : "Head Office (Dhaka)",
              currency: values.currency,
              amountCents: values.amountCents,
              amountInBaseCents: inBase,
              fxRateToBaseE8: fxRate,
              paymentMethod: values.paymentMethod,
              payeeName: values.payeeName || null,
              occurredAt: new Date(values.occurredAt).getTime(),
              notes: values.notes || null,
            }
          : r,
      ),
    );
    setDetail((prev) =>
      prev && prev._id === editingExpense._id
        ? {
            ...prev,
            category: values.category,
            description: values.description,
            branchId: values.branchId,
            branchLabel:
              values.branchId === "branch_factory_savar"
                ? "Factory — Savar"
                : "Head Office (Dhaka)",
            currency: values.currency,
            amountCents: values.amountCents,
            amountInBaseCents: inBase,
            fxRateToBaseE8: fxRate,
            paymentMethod: values.paymentMethod,
            payeeName: values.payeeName || null,
            occurredAt: new Date(values.occurredAt).getTime(),
            notes: values.notes || null,
          }
        : prev,
    );
    setEditingExpense(null);
    toast.success(
      `Updated ${editingExpense.expenseNumber}. Audit trail recorded.`,
    );
  }

  function handleExportCsv(extension: "csv" | "xls") {
    const columns: ExportColumn<MockExpense>[] = [
      { key: "expenseNumber", label: "Expense #" },
      { key: "occurredAt", label: "Date", format: (v) => formatDateForCsv(Number(v)) },
      { key: "category", label: "Category", format: (v) => CATEGORY_LABEL[v as MockExpenseCategory] ?? String(v) },
      { key: "description", label: "Description" },
      { key: "branchLabel", label: "Branch" },
      { key: "amountCents", label: "Amount" },
      { key: "currency", label: "Currency" },
      { key: "amountInBaseCents", label: "Amount BDT" },
      { key: "paymentMethod", label: "Payment method" },
      { key: "payeeName", label: "Payee", format: (v) => (v == null ? "" : String(v)) },
      { key: "status", label: "Status", format: (v) => STATUS_LABEL[v as MockExpenseStatus] ?? String(v) },
      { key: "notes", label: "Notes", format: (v) => (v == null ? "" : String(v)) },
    ];
    downloadCsv(
      exportFilename("expenses", extension),
      toCsv(filtered, columns),
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Expenses"
        description="Operating expense ledger — rent, salaries, utilities, logistics, and more."
        actions={
          <>
            <ExportButton
              onCsv={() => handleExportCsv("csv")}
              onExcel={() => handleExportCsv("xls")}
            />
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> Record expense
            </Button>
          </>
        }
      />

      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        aria-label="Expense KPIs"
      >
        <KpiCard
          caption="Total spend"
          value={formatMoneyCompact(totalThisMonth, "BDT", { locale: "en-BD" })}
          hint="Visible window"
        />
        <KpiCard
          caption="Pending approval"
          value={pendingCount}
          hint={formatMoneyCompact(pendingValue, "BDT", { locale: "en-BD" })}
        />
        <KpiCard
          caption="Top category"
          value={topCategory ? CATEGORY_LABEL[topCategory[0]] : "—"}
          hint={
            topCategory
              ? formatMoneyCompact(topCategory[1], "BDT", { locale: "en-BD" })
              : "No data"
          }
        />
        <KpiCard
          caption="Entries"
          value={rows.length}
          hint="Across all statuses"
        />
      </section>

      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-2">
        <Filter className="h-3.5 w-3.5 text-(--text-tertiary)" aria-hidden />
        <FilterChip
          label="Status"
          active={statusFilter === "all"}
          onClick={() => setStatusFilter("all")}
        >
          All
        </FilterChip>
        {ALL_STATUSES.map((s) => (
          <FilterChip
            key={s}
            label="Status"
            active={statusFilter === s}
            onClick={() => setStatusFilter(s)}
          >
            {STATUS_LABEL[s]}
          </FilterChip>
        ))}
        <span className="mx-2 text-(--text-tertiary) text-[11px]">·</span>
        <FilterChip
          label="Category"
          active={categoryFilter === "all"}
          onClick={() => setCategoryFilter("all")}
        >
          All categories
        </FilterChip>
        {ALL_CATEGORIES.slice(0, 6).map((c) => (
          <FilterChip
            key={c}
            label="Category"
            active={categoryFilter === c}
            onClick={() => setCategoryFilter(c)}
          >
            {CATEGORY_LABEL[c]}
          </FilterChip>
        ))}
      </div>

      <DataTable
        data={filtered}
        columns={columns}
        getRowId={(r) => r._id}
        onRowClick={(row) => setDetail(row.original)}
        emptyTitle="No expenses match"
        emptyDescription="Record an expense to populate the log."
        emptyIcon={<CreditCard className="h-5 w-5" aria-hidden />}
      />

      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent side="right" size="md">
          <SheetHeader>
            <SheetTitle>Record expense</SheetTitle>
            <SheetDescription>
              Foreign-currency expenses snapshot the FX rate at posting time.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <ExpenseForm
              branches={[
                { id: "branch_hq", displayName: "Head Office (Dhaka)" },
                { id: "branch_factory_savar", displayName: "Factory — Savar" },
              ]}
              fxRateE8={fxRateForCurrency("USD")}
              baseCurrency="BDT"
              onSubmit={handleCreate}
              onCancel={() => setCreateOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      <Sheet
        open={detail !== null}
        onOpenChange={(o) => {
          if (!o) {
            setDetail(null);
            setEditingExpense(null);
          }
        }}
      >
        <SheetContent side="right" size="md">
          {detail && editingExpense ? (
            <>
              <SheetHeader>
                <SheetTitle>Edit {editingExpense.expenseNumber}</SheetTitle>
                <SheetDescription>
                  Pending and approved expenses can still be edited; once
                  paid, edits are locked.
                </SheetDescription>
              </SheetHeader>
              <SheetBody>
                <ExpenseForm
                  submitLabel="Save changes"
                  branches={[
                    { id: "branch_hq", displayName: "Head Office (Dhaka)" },
                    {
                      id: "branch_factory_savar",
                      displayName: "Factory — Savar",
                    },
                  ]}
                  fxRateE8={fxRateForCurrency(editingExpense.currency)}
                  baseCurrency="BDT"
                  initialValues={{
                    category: editingExpense.category,
                    description: editingExpense.description,
                    branchId: editingExpense.branchId,
                    amountCents: editingExpense.amountCents,
                    currency: editingExpense.currency,
                    paymentMethod: editingExpense.paymentMethod,
                    externalReference: "",
                    payeeName: editingExpense.payeeName ?? "",
                    occurredAt: new Date(editingExpense.occurredAt)
                      .toISOString()
                      .slice(0, 10),
                    notes: editingExpense.notes ?? "",
                  }}
                  onSubmit={handleEditExpense}
                  onCancel={() => setEditingExpense(null)}
                />
              </SheetBody>
            </>
          ) : detail ? (
            <ExpenseDetail
              expense={detail}
              onEdit={() => setEditingExpense(detail)}
            />
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
  children,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  void label;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "h-7 rounded-full border px-2.5 text-[11px] transition-colors",
        active
          ? "bg-(--accent) text-(--accent-fg) border-(--accent)"
          : "bg-(--bg-canvas) text-(--text-secondary) border-(--border-subtle) hover:bg-(--bg-hover)",
      )}
    >
      {children}
    </button>
  );
}

function ExpenseDetail({
  expense,
  onEdit,
}: {
  expense: MockExpense;
  onEdit: () => void;
}) {
  const canEdit = expense.status !== "paid" && expense.status !== "void";
  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-3 flex-wrap">
          <span className="font-mono">{expense.expenseNumber}</span>
          <StatusPill variant={STATUS_VARIANT[expense.status]} size="sm">
            {STATUS_LABEL[expense.status]}
          </StatusPill>
          <RecordAuditBadge tableName="expenses" recordId={expense._id} />
          <AttachmentBadge
            parentTable="expenses"
            parentRecordId={expense._id}
          />
        </SheetTitle>
        <SheetDescription>{expense.description}</SheetDescription>
      </SheetHeader>
      <SheetBody>
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
            <TabsTrigger value="receipts">Receipts</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-4">
            <div className="grid grid-cols-2 gap-3">
              <DetailField
                label="Category"
                value={CATEGORY_LABEL[expense.category]}
              />
              <DetailField label="Branch" value={expense.branchLabel} />
              <DetailField
                label="Amount"
                value={formatMoney(expense.amountCents, expense.currency, {
                  display: expense.currency === "BDT" ? "symbol" : "code",
                  locale: "en-BD",
                })}
              />
              <DetailField
                label="In BDT"
                value={formatMoney(expense.amountInBaseCents, "BDT", {
                  locale: "en-BD",
                })}
              />
              <DetailField
                label="Method"
                value={expense.paymentMethod.replace(/_/g, " ")}
              />
              <DetailField label="Payee" value={expense.payeeName ?? "—"} />
              <DetailField
                label="Occurred"
                value={formatDate(expense.occurredAt, { style: "medium" })}
              />
            </div>
            {expense.notes ? (
              <section className="mt-4">
                <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-1">
                  Notes
                </div>
                <p className="text-[13px] text-(--text-secondary) whitespace-pre-line">
                  {expense.notes}
                </p>
              </section>
            ) : null}
          </TabsContent>
          <TabsContent value="history" className="mt-4">
            <RecordAuditTrail tableName="expenses" recordId={expense._id} />
          </TabsContent>
          <TabsContent value="receipts" className="mt-4">
            <AttachmentList
              parentTable="expenses"
              parentRecordId={expense._id}
              purposeOptions={EXPENSE_PURPOSE_OPTIONS}
              mimeAllowlist={["image/*", "application/pdf"]}
              title="Drop receipt here"
              description="Photo of physical receipt, e-bill PDF, or supporting docs. Max 10 MB."
            />
          </TabsContent>
        </Tabs>
      </SheetBody>
      <SheetFooter>
        {canEdit ? (
          <Button variant="ghost" size="icon" onClick={onEdit} aria-label="Edit">
            <Pencil className="size-4" />
          </Button>
        ) : (
          <Tooltip content="Cannot edit closed records — create a credit note instead">
            <span>
              <Button variant="ghost" size="icon" disabled aria-label="Edit">
                <Pencil className="size-4" />
              </Button>
            </span>
          </Tooltip>
        )}
      </SheetFooter>
    </>
  );
}

function DetailField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </div>
      <div className="text-[13px] text-(--text-primary) font-mono tabular-nums capitalize">
        {value}
      </div>
    </div>
  );
}
